import { type FormEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { SocialButtons } from '../components/SocialButtons';
import { captchaHeaders, Turnstile } from '../components/Turnstile';
import { Alert, AuthLayout, Button, Field, LegalNotice, TextLink } from '../components/ui';
import { authClient, authErrorMessage, returnPath, siteUrl, withNext } from '../lib/auth-client';
import { usePublicConfig } from '../lib/public-config';
import { useSession } from '../lib/session';

/** Social sign-in comes back with `?error=<code>` when the API refuses it. */
export function socialSignInError(code: string | null): string | null {
  if (!code) return null;
  switch (code) {
    case 'account_not_linked':
      return 'This email already has a MOGZO account. Sign in with your email and password below, or use “Forgot password?” to set one.';
    case 'access_denied':
      return 'Sign-in was cancelled. Try again, or use your email.';
    case 'email_not_found':
      return 'Your Google account didn’t share an email address, so we can’t sign you in with it.';
    case 'please_restart_the_process':
    case 'state_mismatch':
      return 'That sign-in took too long or was opened in another tab. Please try again.';
    default:
      return 'That sign-in didn’t work. Try again or use your email.';
  }
}

/** Only same-site paths, so `?next=` can't send people to another website. */
export function safeNext(next: string | null): string {
  return returnPath(next) ?? '/account';
}

export function SignIn() {
  const config = usePublicConfig();
  const { refresh } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const back = returnPath(params.get('next'));
  const next = back ?? '/account';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(socialSignInError(params.get('error')));

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const { data, error: failed } = await authClient.signIn.email({
      email: email.trim(),
      password,
      callbackURL: siteUrl(withNext('/verify-email', back)),
      fetchOptions: { headers: captchaHeaders(captcha) },
    });
    setBusy(false);
    if (failed) {
      setError(authErrorMessage(failed));
      setCaptchaReset((n) => n + 1);
      return;
    }
    if (data && 'twoFactorRedirect' in data && data.twoFactorRedirect) {
      navigate(`/sign-in/two-factor?next=${encodeURIComponent(next)}`);
      return;
    }
    const me = await refresh();
    navigate(me && !me.profileComplete ? withNext('/complete-profile', back) : next);
  }

  return (
    <AuthLayout
      title="Sign in"
      footer={
        <>
          New to MOGZO? <TextLink to={withNext('/sign-up', back)}>Create an account</TextLink>
        </>
      }
    >
      <SocialButtons providers={config?.socialProviders ?? []} next={back} />
      <form onSubmit={onSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <Field
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <div className="text-right text-sm">
          <TextLink to="/forgot-password">Forgot password?</TextLink>
        </div>
        <Turnstile
          siteKey={config?.turnstileSiteKey ?? null}
          onToken={setCaptcha}
          resetKey={captchaReset}
        />
        <Button busy={busy} disabled={!!config?.turnstileSiteKey && !captcha}>
          Sign in
        </Button>
      </form>
      <LegalNotice action="continuing" />
    </AuthLayout>
  );
}
